/**
 * SPEC-20260507-2030: tela de admin pra gerenciamento de credenciais IA.
 *
 * - Lista os 4 providers reais (anthropic/openai/gemini/bedrock).
 * - Por linha: badge de status (DB / env legacy / não configurado), última
 *   rotação, último teste, botões "configurar/atualizar", "testar", "remover".
 * - Form modal por provider com schema apropriado (Bedrock pede region em
 *   extraConfig).
 *
 * RBAC: backend exige `platform-master`; UI usa <RequireRole anyOf={['platform-master']}>.
 */

import { useEffect, useState } from "react";
import { Loader2, KeyRound, RefreshCw, Trash2, CheckCircle2, AlertCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import {
  listAICredentials,
  upsertAICredential,
  deleteAICredential,
  testAICredential,
  type CredentialListResponse,
  type CredentialView,
  type SupportedProvider,
} from "../services/ai-credentials";
import { PageHeader } from "../components/layout/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RequireRole } from "../components/RequireRole";

const PROVIDER_LABELS: Record<SupportedProvider, string> = {
  anthropic: "Anthropic Claude",
  openai: "OpenAI",
  gemini: "Google Gemini",
  bedrock: "AWS Bedrock",
};

export default function AICredentialsPage() {
  return (
    <RequireRole anyOf={["platform-master"]}>
      <AICredentialsContent />
    </RequireRole>
  );
}

function AICredentialsContent() {
  const [data, setData] = useState<CredentialListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<SupportedProvider | null>(null);
  const [testing, setTesting] = useState<SupportedProvider | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      const r = await listAICredentials();
      setData(r);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Erro ao listar credenciais");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function handleTest(provider: SupportedProvider) {
    setTesting(provider);
    try {
      const r = await testAICredential(provider);
      if (r.ok) toast.success(`${PROVIDER_LABELS[provider]} OK (${r.latencyMs}ms)`);
      else toast.error(`${PROVIDER_LABELS[provider]} falhou: ${r.error}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro no teste");
    } finally {
      setTesting(null);
      void refresh();
    }
  }

  async function handleDelete(provider: SupportedProvider) {
    if (!confirm(`Remover credencial de ${PROVIDER_LABELS[provider]}? Esta ação não pode ser desfeita.`))
      return;
    try {
      await deleteAICredential(provider);
      toast.success(`${PROVIDER_LABELS[provider]} removido`);
      void refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao remover");
    }
  }

  return (
    <>
      <PageHeader
        title="Credenciais de IA"
        subtitle="Gerencie chaves dos providers (Anthropic, OpenAI, Gemini, Bedrock). Encrypted at-rest."
      />

      {err && (
        <div className="rounded border border-danger/30 bg-danger-soft/40 p-3 text-sm text-danger-soft-fg mb-4">
          {err}
        </div>
      )}

      {data && !data.cryptoEnabled && (
        <div className="rounded border border-warning/30 bg-warning-soft/40 p-3 text-sm flex items-start gap-2 mb-4">
          <AlertCircle size={16} className="text-warning shrink-0 mt-0.5" />
          <div className="text-warning-soft-fg">
            <strong>Encryption desabilitada:</strong> {data.cryptoMessage}
            <p className="mt-1 text-xs opacity-90">
              Sem master key, novos credentials não podem ser salvos via UI. Configure{" "}
              <code className="font-mono-tight">WOS_ENCRYPTION_KEY</code> (32 bytes base64) no
              backend e reinicie. Env vars existentes continuam funcionando como fallback.
            </p>
          </div>
        </div>
      )}

      {loading && !data && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
          <Loader2 size={14} className="animate-spin" /> carregando…
        </div>
      )}

      {data && (
        <div className="space-y-3">
          {data.providers.map((p) => (
            <ProviderCard
              key={p.provider}
              provider={p}
              cryptoEnabled={data.cryptoEnabled}
              onEdit={() => setEditing(p.provider)}
              onTest={() => handleTest(p.provider)}
              onDelete={() => handleDelete(p.provider)}
              testing={testing === p.provider}
            />
          ))}
        </div>
      )}

      {editing && (
        <CredentialModal
          provider={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void refresh();
          }}
        />
      )}
    </>
  );
}

interface ProviderCardProps {
  provider: CredentialView;
  cryptoEnabled: boolean;
  onEdit(): void;
  onTest(): void;
  onDelete(): void;
  testing: boolean;
}

function ProviderCard({ provider, cryptoEnabled, onEdit, onTest, onDelete, testing }: ProviderCardProps) {
  const dbConfigured = provider.configured;
  const envOnly = !dbConfigured && provider.envFallbackPresent;
  const notConfigured = !dbConfigured && !envOnly;

  return (
    <div className="rounded border border-border bg-surface-1 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 min-w-0">
          <KeyRound size={14} className="text-muted-foreground shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-medium text-sm">{PROVIDER_LABELS[provider.provider]}</h3>
              {dbConfigured && (
                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-success-soft text-success-soft-fg border border-success/20">
                  Configurado (DB)
                </span>
              )}
              {envOnly && (
                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-warning-soft text-warning-soft-fg border border-warning/20">
                  Configurado (env legacy)
                </span>
              )}
              {notConfigured && (
                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-surface-2 text-muted-foreground border border-border">
                  Não configurado
                </span>
              )}
              {provider.lastTestStatus === "ok" && (
                <span className="text-[10px] flex items-center gap-1 px-1.5 py-0.5 rounded text-success-soft-fg">
                  <CheckCircle2 size={10} /> Teste OK
                </span>
              )}
              {provider.lastTestStatus === "failed" && (
                <span className="text-[10px] flex items-center gap-1 px-1.5 py-0.5 rounded text-danger-soft-fg">
                  <AlertCircle size={10} /> Teste falhou
                </span>
              )}
            </div>
            <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
              {provider.lastRotatedAt && (
                <span>
                  Última rotação:{" "}
                  <time>{new Date(provider.lastRotatedAt).toLocaleString("pt-BR")}</time>
                </span>
              )}
              {provider.lastUsedAt && (
                <span>
                  Último uso: <time>{new Date(provider.lastUsedAt).toLocaleString("pt-BR")}</time>
                </span>
              )}
              {provider.extraConfigKeys.length > 0 && (
                <span>extra: {provider.extraConfigKeys.join(", ")}</span>
              )}
            </div>
            {provider.lastTestError && (
              <div className="text-xs text-danger-soft-fg mt-1">
                Último erro: <code className="font-mono-tight">{provider.lastTestError.slice(0, 200)}</code>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Button size="sm" variant="outline" onClick={onTest} disabled={testing || (!dbConfigured && !envOnly)}>
            {testing ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            Testar
          </Button>
          <Button size="sm" variant="outline" onClick={onEdit} disabled={!cryptoEnabled}>
            <RefreshCw size={12} />
            {dbConfigured ? "Atualizar" : "Configurar"}
          </Button>
          {dbConfigured && (
            <Button size="sm" variant="outline" onClick={onDelete}>
              <Trash2 size={12} />
              Remover
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

interface CredentialModalProps {
  provider: SupportedProvider;
  onClose(): void;
  onSaved(): void;
}

function CredentialModal({ provider, onClose, onSaved }: CredentialModalProps) {
  const [apiKey, setApiKey] = useState("");
  const [region, setRegion] = useState(provider === "bedrock" ? "us-east-1" : "");
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    if (!apiKey.trim()) {
      toast.error("API key obrigatória");
      return;
    }
    setSaving(true);
    try {
      const extraConfig: Record<string, string> = {};
      if (provider === "bedrock" && region) extraConfig.region = region;
      await upsertAICredential(provider, {
        apiKey: apiKey.trim(),
        extraConfig: Object.keys(extraConfig).length > 0 ? extraConfig : undefined,
      });
      toast.success(`${PROVIDER_LABELS[provider]} configurado`);
      onSaved();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-surface-1 rounded-lg shadow-lg w-full max-w-md p-4 border border-border"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-medium text-base mb-3">Configurar {PROVIDER_LABELS[provider]}</h2>

        <div className="space-y-3">
          <div>
            <Label htmlFor="apiKey">API Key</Label>
            <Input
              id="apiKey"
              type="password"
              placeholder={hintFor(provider)}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              autoFocus
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              {provider === "bedrock"
                ? "Bedrock API Key (Bearer Token) — gerada em Console → Amazon Bedrock → API keys."
                : "Será encrypted at-rest com AES-256-GCM. Apenas platform-master vê esse formulário."}
            </p>
          </div>

          {provider === "bedrock" && (
            <div>
              <Label htmlFor="region">Região AWS</Label>
              <Input
                id="region"
                placeholder="sa-east-1, us-east-1, ..."
                value={region}
                onChange={(e) => setRegion(e.target.value)}
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Região onde a chave foi gerada. Modelos Claude variam por região.
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving || !apiKey.trim()}>
            {saving && <Loader2 size={12} className="animate-spin" />}
            Salvar
          </Button>
        </div>
      </div>
    </div>
  );
}

function hintFor(provider: SupportedProvider): string {
  switch (provider) {
    case "anthropic":
      return "sk-ant-...";
    case "openai":
      return "sk-...";
    case "gemini":
      return "AIza...";
    case "bedrock":
      return "ABSK... (Bearer Token)";
  }
}
